import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { config } from '../../config/config';
import { showToast } from '../../components/utils/Toast';

const authHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`
});

const json = async (res) => {
  const data = await res.json();
  if (!res.ok) throw new Error(data.message || 'Request failed');
  return data;
};

export const fetchCalendars = createAsyncThunk(
  'calendars/fetch',
  async (_, { rejectWithValue }) => {
    try {
      const res = await fetch(`${config.apiUrl}/api/calendars`, { headers: authHeader() });
      const data = await json(res);
      return data.calendars;
    } catch (e) { return rejectWithValue(e.message); }
  }
);

export const createCalendar = createAsyncThunk(
  'calendars/create',
  async (payload, { rejectWithValue }) => {
    try {
      const res = await fetch(`${config.apiUrl}/api/calendars`, {
        method: 'POST',
        headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await json(res);
      return data.calendar;
    } catch (e) { return rejectWithValue(e.message); }
  }
);

export const updateCalendar = createAsyncThunk(
  'calendars/update',
  async ({ id, updates }, { rejectWithValue }) => {
    try {
      const res = await fetch(`${config.apiUrl}/api/calendars/${id}`, {
        method: 'PUT',
        headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      const data = await json(res);
      return data.calendar;
    } catch (e) { return rejectWithValue(e.message); }
  }
);

export const deleteCalendar = createAsyncThunk(
  'calendars/delete',
  async (id, { rejectWithValue }) => {
    try {
      const res = await fetch(`${config.apiUrl}/api/calendars/${id}`, {
        method: 'DELETE', headers: authHeader()
      });
      await json(res);
      return id;
    } catch (e) { return rejectWithValue(e.message); }
  }
);

const slice = createSlice({
  name: 'calendars',
  initialState: {
    items: [],
    visibleIds: null, // null => all visible
    loading: false,
    error: null
  },
  reducers: {
    toggleCalendarVisibility(state, action) {
      const id = action.payload;
      const all = state.items.map((c) => c._id);
      const current = state.visibleIds || all;
      if (current.includes(id)) {
        state.visibleIds = current.filter((x) => x !== id);
      } else {
        state.visibleIds = [...current, id];
      }
    },
    setVisibleCalendars(state, action) {
      state.visibleIds = action.payload;
    }
  },
  extraReducers: (b) => {
    b.addCase(fetchCalendars.pending, (s) => { s.loading = true; });
    b.addCase(fetchCalendars.fulfilled, (s, a) => { s.loading = false; s.items = a.payload; });
    b.addCase(fetchCalendars.rejected, (s, a) => { s.loading = false; s.error = a.payload; });
    b.addCase(createCalendar.fulfilled, (s, a) => { s.items.push(a.payload); showToast('Calendar created', 'success'); });
    b.addCase(createCalendar.rejected, (_, a) => { showToast(a.payload, 'error'); });
    b.addCase(updateCalendar.fulfilled, (s, a) => {
      const i = s.items.findIndex((c) => c._id === a.payload._id);
      if (i >= 0) s.items[i] = a.payload;
      showToast('Calendar updated', 'success');
    });
    b.addCase(deleteCalendar.fulfilled, (s, a) => {
      s.items = s.items.filter((c) => c._id !== a.payload);
      showToast('Calendar deleted', 'success');
    });
    b.addCase(deleteCalendar.rejected, (_, a) => { showToast(a.payload, 'error'); });
  }
});

export const { toggleCalendarVisibility, setVisibleCalendars } = slice.actions;
export default slice.reducer;
